import { minorExponent } from "@/lib/currency";

export type CachedFxRate = {
  rate: number;
  timestamp: number;
};

export const FX_CACHE_PREFIX = "gotravel:fx_cache:";

/**
 * Converts an amount from one currency to another using a whole-unit rate
 * (1 unit of `fromCurrency` = `rate` units of `toCurrency`).
 * Corrects for differences in minor-unit exponents.
 */
export function convertCurrencyMinor(
  amountMinor: number,
  fromCurrency: string,
  toCurrency: string,
  rate: number
): number {
  if (!Number.isFinite(rate) || rate <= 0) {
    throw new Error("환율이 올바르지 않습니다");
  }
  const fromExp = minorExponent(fromCurrency);
  const toExp = minorExponent(toCurrency);
  const scale = Math.pow(10, toExp - fromExp);
  return Math.round(amountMinor * rate * scale);
}

function getStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  if (typeof globalThis !== "undefined" && (globalThis as any).localStorage) {
    return (globalThis as any).localStorage as Storage;
  }
  return null;
}

/**
 * Loads a cached FX rate from localStorage. Returns null if not found or invalid.
 */
export function loadCachedFxRate(from: string, to: string): CachedFxRate | null {
  const f = from.trim().toUpperCase();
  const t = to.trim().toUpperCase();
  if (f === t) {
    return { rate: 1, timestamp: Date.now() };
  }
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(`${FX_CACHE_PREFIX}${f}_${t}`);
    if (!raw) return null;
    const data = JSON.parse(raw) as Partial<CachedFxRate>;
    if (typeof data.rate === "number" && data.rate > 0 && typeof data.timestamp === "number") {
      return { rate: data.rate, timestamp: data.timestamp };
    }
  } catch {
    // Ignore parse / storage errors
  }
  return null;
}

/**
 * Saves an FX rate to localStorage cache.
 */
export function saveCachedFxRate(from: string, to: string, rate: number): void {
  const f = from.trim().toUpperCase();
  const t = to.trim().toUpperCase();
  if (f === t || !Number.isFinite(rate) || rate <= 0) return;
  const storage = getStorage();
  if (!storage) return;
  try {
    const data: CachedFxRate = {
      rate,
      timestamp: Date.now(),
    };
    storage.setItem(`${FX_CACHE_PREFIX}${f}_${t}`, JSON.stringify(data));

    // Also cache inverse rate for convenience
    const inverseData: CachedFxRate = {
      rate: 1 / rate,
      timestamp: Date.now(),
    };
    storage.setItem(`${FX_CACHE_PREFIX}${t}_${f}`, JSON.stringify(inverseData));
  } catch {
    // Ignore storage quota errors
  }
}

